import { createInertiaApp } from "@inertiajs/react"
import { type ReactNode, createElement } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"
import PartnerLayout from "@/Layouts/PartnerLayout"
import ErrorBoundary from "@/components/shared/ErrorBoundary"
import './progress-bar.css'

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

// @ts-ignore
const appName = (import.meta.env.VITE_APP_NAME ?? "Rails") as string

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => title,

  resolve: async (name) => {
    // Lazy load - Vite creates separate chunks per page
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.jsx")

    const importPage = pages[`../pages/${name}.jsx`]
    if (!importPage) {
      console.error(`Missing Inertia page component: '${name}.jsx'`)
      throw new Error(`Missing Inertia page component: '${name}.jsx'`)
    }

    const page = await importPage()

    // Skip layout for partner auth pages
    if (name.startsWith("partner/auth")) {
      return page
    }

    // Dynamic layout assignment for partner pages
    if (name.startsWith("partner") && page?.default) {
      page.default.layout = (pageContent: any) => {
        return createElement(PartnerLayout, null, pageContent)
      }
    }

    return page
  },

  setup({ el, App, props }) {
    if (el) {
      const appWithErrorBoundary = createElement(ErrorBoundary, null, createElement(App, props))

      if (el.hasChildNodes()) {
        hydrateRoot(el, appWithErrorBoundary)
        return
      }
      createRoot(el).render(appWithErrorBoundary)
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }
  },

  progress: {
    delay: 250,
    color: '#3B5B8B',
    includeCSS: false,
    showSpinner: false,
  },
})
